import { translateBackend } from '@assets/i18n/config';
import type { Account, AccountTypes } from '@interfaces/account';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import {
  ConfirmationModal,
  ConfirmButtonColors,
} from '@ovComponents/3-pattern/confirmationDialog/confirmationDialog';
import { SelectionTile } from '@ovComponents/3-pattern/selectionTile/selectionTile';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export const useAccountCreationMethodChoice = () => {
  type MethodChoiceResult = {
    choice: typeof selectedMethod;
    closeDialog: () => void;
  };
  const [resolver, setResolver] = useState<
    ((result: MethodChoiceResult) => void) | null
  >(null);
  const hasPending = !!resolver;

  /** Modal state */
  const [selectedMethod, setSelectedMethod] = useState<
    | { method: 'NEW_ACCOUNT' }
    | { method: 'NEW_SUB_ACCOUNT'; account: Account }
    | null
  >(null);
  const [accountType, setAccountType] = useState<AccountTypes | null>(null);
  const [activeAccounts, setActiveAccounts] = useState<Account[]>([]);
  const [allowNewAccount, setAllowNewAccount] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(!selectedMethod);
  const [disableCancelButton, setDisableCancelButton] =
    useState<boolean>(false);
  /** ************* */

  const { t } = useTranslation('workflowCompletions');
  const accountTypeDisplay = t(`accountTypes:${accountType}`);

  useEffect(() => {
    setLoading(!selectedMethod);
  }, [selectedMethod]);

  const closeDialog = () => {
    setDisableCancelButton(false);
    setResolver(null);
    setSelectedMethod(null);
    setAccountType(null);
    setAllowNewAccount(false);
  };

  const getAccountCreationMethodChoice = async (args: {
    accountType: AccountTypes;
    activeAccounts: Account[];
    allowNewAccount: boolean;
  }) =>
    new Promise<MethodChoiceResult>((resolve) => {
      setAccountType(args.accountType);
      setActiveAccounts(args.activeAccounts);
      setResolver(() => resolve);
      setAllowNewAccount(args.allowNewAccount);
    });

  return {
    getAccountCreationMethodChoice,
    accountCreationMethodChoiceDialog: (
      <ConfirmationModal
        title={t('createAccountForGoal.accountCreationMethodChoice.title')}
        confirmButtonLabel={t(
          'createAccountForGoal.accountCreationMethodChoice.confirmButtonLabel',
        )}
        open={hasPending}
        confirmButtonColor={ConfirmButtonColors.primary}
        onConfirm={() => {
          setDisableCancelButton(true);
          resolver?.({
            choice: selectedMethod,
            closeDialog,
          });
        }}
        onCancel={() => {
          resolver?.({
            choice: null,
            closeDialog,
          });
        }}
        loading={loading}
        disableCancelButton={disableCancelButton}
      >
        <Typography variant='bodyLarge'>
          {allowNewAccount
            ? t('createAccountForGoal.accountCreationMethodChoice.body', {
                numAccounts: activeAccounts?.length,
                accountType: accountTypeDisplay,
              })
            : t(
                'createAccountForGoal.accountCreationMethodChoice.bodyNoNewAccount',
                {
                  numAccounts: activeAccounts?.length,
                  accountType: accountTypeDisplay,
                },
              )}
        </Typography>
        <SelectionTile
          sx={{ mt: 2 }}
          type='radio'
          options={[
            ...activeAccounts.map((account) => ({
              label: t(
                'createAccountForGoal.accountCreationMethodChoice.newSubAccount',
                {
                  accountName: account.translatedNickName
                    ? translateBackend(account.translatedNickName)
                    : accountTypeDisplay,
                  accountId: `***${account.id.slice(-4)}`,
                },
              ),
              value: account.id,
            })),
            ...(allowNewAccount
              ? [
                  {
                    label: t(
                      'createAccountForGoal.accountCreationMethodChoice.newAccount',
                      { accountType: accountTypeDisplay },
                    ),
                    value: 'NEW_ACCOUNT',
                  },
                ]
              : []),
          ]}
          value={
            selectedMethod?.method === 'NEW_ACCOUNT'
              ? 'NEW_ACCOUNT'
              : (selectedMethod?.account?.id as string)
          }
          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
            if (e.target.value === 'NEW_ACCOUNT') {
              setSelectedMethod({ method: 'NEW_ACCOUNT' });
            } else {
              setSelectedMethod({
                method: 'NEW_SUB_ACCOUNT',
                account: activeAccounts.find(
                  (account) => account.id === e.target.value,
                ) as Account,
              });
            }
          }}
        />
      </ConfirmationModal>
    ),
  };
};
